import type { BaseRange, Editor } from 'slate'
import type { Dispatch, RefObject } from 'react'
import { Range, Transforms } from 'slate'
import { ReactEditor } from 'slate-react'
import { debounce, partition } from 'lodash-es'
import { useEffect } from 'react'
import Fuse from 'fuse.js'

import type { ErrorActionType, LinkTooltipActionTypes } from './LinkTooltipContext'
import { SET_ERROR_STATE, SET_IS_VIEW_MODE, SET_LINK_DETAILS } from './LinkTooltip.constants'
import { formatHref } from '../../../utils/link-sanitizer'
import { getButton, insertButton, isButtonFocused } from '../../text/buttons'
import { getLink, insertLink, isExternalURL, isLinkFocused, isValidHref } from '../../text/links'

/**
 * Flattens a nested array of items and extracts specified properties from each item.
 *
 * This function takes an array of items, each potentially containing nested children,
 * and returns a flat array of new items containing only the specified properties.
 * Optionally, it can translate the values of specified properties using a provided translation function.
 *
 * @param arr - The array of items to flatten and extract properties from.
 * @param properties - The list of properties to extract from each item.
 * @param translate - An optional function to translate the values of specified properties.
 * @returns A flat array of new items containing only the specified properties.
 *
 * @example
 * const items = [{ id: '1', title: 'Item 1', slug: 'item-1', href: '/item-1', children: [{ id: '2', title: 'Item 2', slug: 'item-2', href: '/item-2' }] }, { id: '3', title: 'Item 3', slug: 'item-3', href: '/item-3' }];
 * const properties = ['title', 'slug'];
 * const result = flattenAndExtractProperties(items, properties);
 * // result: [
 * //   { title: 'Item 1', slug: 'item-1' },
 * //   { title: 'Item 2', slug: 'item-2' },
 * //   { title: 'Item 3', slug: 'item-3' }
 * // ]
 *
 * @example
 * const items = [{ title: 'enumerations.legalPages.privacyPolicy', slug: 'privacy', href: '/privacy' }];
 * const properties = ['title', 'slug'];
 * const result = flattenAndExtractProperties(items, properties, (title) => `${title}`);
 * // result: [
 * //   { title: 'Privacy Policy', slug: 'privacy' },
 * // ]
 */

export function flattenAndExtractProperties(
  arr,
  properties: string[],
  translate?: (key: string) => string,
): ItemType[] {
  if (!Array.isArray(arr)) {
    return []
  }

  return arr.reduce((acc, item) => {
    const newItem = {}
    properties.forEach((property) => {
      newItem[property] = property === 'title' && translate ? translate(item[property]) : item[property]
    })

    if (Array.isArray(item.children)) {
      acc.push(...flattenAndExtractProperties(item.children, properties, translate))
    }

    acc.push(newItem)
    return acc
  }, [])
}

/**
 * Creates a link builder function based on the provided pathname.
 *
 * @param pathname - Used to extract language prefix from the pathname if any.
 * @returns A function that generates a link for a given item and type.
 *
 * @example
 * const linkBuilder = createLinkBuilder('/cs/c/whatever-page');
 * const link = linkBuilder({ slug: 'category-1' }, 'category');
 * // link: '/cs/c/category-1'
 *
 * @example
 * const linkBuilder = createLinkBuilder('/');
 * const link = linkBuilder({ slug: 'about' }, 'content');
 * // link: '/i/about'
 *
 * @example
 * const linkBuilder = createLinkBuilder('/en');
 * const link = linkBuilder({ href: '/' }, 'home');
 * // link: '/en'
 */
export function createLinkBuilder(pathname: string) {
  const PREFIXES: { [key: string]: string } = {
    product: 'p/',
    content: 'i/',
    footer: 'i/',
    legal: 'l/',
    category: 'c/',
  }

  const normalize = (url: string) => url.replace(/\/+/g, '/').replace(/\/$/, '') || '/'

  return function (item: NormalizedItem, type: string): string {
    const prefix = PREFIXES[type] ?? ''
    const langMatch = /^\/(\w{2})(\/|$)/.exec(pathname)
    const basePath = langMatch ? `/${langMatch[1]}` : ''

    if (item.href === '/') {
      return normalize(basePath)
    }

    return normalize(`${basePath}/${prefix}${item.slug ?? ''}`)
  }
}

/**
 * Normalizes multiple arrays of items by extracting specified properties.
 *
 * @param itemsArray - An array of objects, each containing an array of items, a title key, and a type.
 * @param linkBuilder - A function to generate the link for each item.
 * @returns An array of arrays of normalized items.
 *
 * @example
 * const itemsArray = [
 *   { items: [{ id: "d703dd0b-73fe-4f5c-9800-614afbbd8b09", title: 'Page 1', slug: 'page-1' }], titleKey: 'title', type: 'page' },
 *   { items: [{ productId: "7f5938b7-a46c-4f67-b1dc-d9210de4dd64" name: 'Product 1', slug: 'product-1' }], titleKey: 'name', type: 'product' }
 * ];
 * const result = normalizeMultipleItems(itemsArray, (item, type) => `/path/${item.slug}`);
 * // result: [
 * //   [{ id: "d703dd0b-73fe-4f5c-9800-614afbbd8b09" title: 'Page 1', slug: 'page-1', type: 'page', link: '/path/page-1' }],
 * //   [{ id: "7f5938b7-a46c-4f67-b1dc-d9210de4dd64" title: 'Product 1', slug: 'product-1', type: 'product', link: '/path/product-1' }]
 * // ]
 */
export function normalizeMultipleItems(
  itemsArray: ItemsArray[],
  linkBuilder: (item: NormalizedItem, type: string) => string,
) {
  return itemsArray.map(({ items, id, titleKey, type }) => {
    if (!Array.isArray(items)) {
      return []
    }

    return items
      .map((item) => ({
        id: item[id],
        title: item[titleKey],
        slug: item.slug ?? '',
        type: item.href === '/' ? 'home' : type,
        link: linkBuilder(item, type),
      }))
      .sort((a, b) => a.title?.localeCompare(b.title))
  })
}

/**
 * Options for fuzzy search.
 */
const FUZZY_SEARCH_OPTIONS: FuzzySearchOptions = {
  keys: ['title'],
  includeScore: true,
  threshold: 0.45, // This threshold determines how fuzzy the search should be. Lower values mean stricter matching.
}

/**
 * Filters multiple arrays of items based on a search term using fuzzy search.
 *
 * @param itemGroups - An array of arrays of items to filter.
 * @param searchTerm - The term to search for.
 * @param options - The options for fuzzy search.
 * @returns An array of arrays of items that match the search term.
 */
export function filterMultipleItems(itemGroups: ItemType[][], searchTerm: string): ItemType[][] {
  if (!searchTerm.trim()) return itemGroups

  return itemGroups.map((items) => {
    const fuse = new Fuse(items, FUZZY_SEARCH_OPTIONS)
    return fuse.search(searchTerm).map((result) => result.item)
  })
}

/**
 * Sorts an array of items so that item with the type 'home' is at the beginning.
 *
 * @param {ItemType[]} items - The array of items to be sorted. Each item should have a 'type' property.
 * @returns {ItemType[]} - A new array with items of type 'home' at the beginning, followed by other items.
 *
 * @example
 * const items: ItemType[] = [
 *   { id: 1, type: 'footer', title: 'Item 1', link: '/link/i/item-1', slug: 'item-1' },
 *   { id: 2, type: 'home', title: 'Item 2', link: '/link/i/item-2', slug: 'item-2' },
 *   { id: 3, type: 'legal', title: 'Item 3', link: '/link/i/item-3', slug: 'item-3' },
 * ];
 *
 * const sortedItems = sortItemsToStartWithHomeType(items);
 * // Output:
 * // [
 * //   { id: 2, type: 'home', title: 'Item 2', link: '/link/i/item-2', slug: 'item-2' },
 * //   { id: 1, type: 'footer', title: 'Item 1', link: '/link/i/item-1', slug: 'item-1' },
 * //   { id: 3, type: 'legal', title: 'Item 3', link: '/link/i/item-3', slug: 'item-3' },
 * // ]
 */
export function sortItemsToStartWithHomeType(items: ItemType[]): ItemType[] {
  const [homeItems, otherItems] = partition(items, (item) => item.type === 'home')
  return [...homeItems, ...otherItems]
}

export function handleInsertLinkCommon(
  editor: Editor,
  lastSelection: RefObject<BaseRange | undefined>,
  linkTooltipState: LinkTooltipState,
  linkTooltipDispatch: Dispatch<LinkTooltipActionTypes>,
  errorDispatch: Dispatch<ErrorActionType>,
  isButton: boolean,
) {
  if (lastSelection.current) Transforms.select(editor, lastSelection.current)
  ReactEditor.focus(editor)
  if (!isValidHref(linkTooltipState.url)) {
    errorDispatch({ type: SET_ERROR_STATE, payload: { hasError: true } })
    return
  }

  const formattedUrl = formatHref(linkTooltipState.url)
  const isExternal = isExternalURL(formattedUrl)

  if (isButton ? isButtonFocused(editor) : isLinkFocused(editor)) {
    const [element, path] = isButton ? getButton(editor) : getLink(editor)
    Transforms.setNodes(
      editor,
      {
        ...element,
        isExternal,
        url: formattedUrl,
      },
      { at: path },
    )
    Transforms.select(editor, path)

    linkTooltipDispatch({ type: SET_IS_VIEW_MODE, payload: true })
  } else {
    if (isButton) {
      insertButton(editor, linkTooltipState.url)
    } else {
      insertLink(editor, linkTooltipState.url)
    }
  }
  const isCollapsed = editor.selection && Range.isCollapsed(editor.selection)

  if (!isCollapsed) {
    window.requestAnimationFrame(() => {
      linkTooltipDispatch({ type: SET_IS_VIEW_MODE, payload: true })
    })
  }

  window.requestAnimationFrame(() => {
    linkTooltipDispatch({ type: SET_IS_VIEW_MODE, payload: true })
  })
}

export function useLinkDetailsEffect(
  editor: Editor,
  isOpen: boolean,
  linkTooltipState: LinkTooltipState,
  linkTooltipDispatch: Dispatch<LinkTooltipActionTypes>,
  toolbarDispatch: Dispatch<any>,
  isElementFocused: (editor: Editor) => boolean,
  getElement: (editor: Editor) => EditorElement[],
  openTooltipType: string,
  closeTooltipType: string,
) {
  useEffect(() => {
    const debouncedUpdate = debounce(() => {
      if (ReactEditor.isFocused(editor)) {
        if (isElementFocused(editor)) {
          const [element] = getElement(editor)
          if (!isOpen || element.url !== linkTooltipState.url) {
            toolbarDispatch({ type: openTooltipType })
            linkTooltipDispatch({
              type: SET_LINK_DETAILS,
              payload: {
                url: element.url,
                isExternalLink: element.isExternal,
                isViewMode: true,
              },
            })
          }
        }
        if (!isElementFocused(editor) && isOpen) {
          toolbarDispatch({ type: closeTooltipType })
          linkTooltipDispatch({ type: SET_IS_VIEW_MODE, payload: false })
        }
      }
    }, 50)

    debouncedUpdate()
    return () => debouncedUpdate.cancel()
  }, [
    editor,
    editor.selection,
    isElementFocused,
    isOpen,
    linkTooltipState,
    getElement,
    toolbarDispatch,
    closeTooltipType,
    openTooltipType,
    linkTooltipDispatch,
  ])
}

type ItemTypeUnion = 'home' | 'product' | 'content' | 'footer' | 'legal' | 'category' | 'external'
export interface ItemType {
  link: string
  slug: string
  id: string
  title: string
  type: ItemTypeUnion
}

interface BaseNormalizedItem {
  id?: string
  slug: string
  title?: string
  href?: string
}

interface SuggestedSearchProductItem extends BaseNormalizedItem {
  productId: string
  name: string
}

export type NormalizedItem = BaseNormalizedItem | SuggestedSearchProductItem

export interface ItemsArray {
  items: NormalizedItem[]
  id: string
  titleKey: string
  type: ItemTypeUnion
}

export interface LinkTooltipState {
  url: string
  title: string
  type: string
  inputValue: string
  searchTerm: string
  isExternalLink: boolean
  isViewMode: boolean
  isEditing: boolean
}

export interface CategoryState {
  selectedCategory: string | null
  showCategories: boolean
  searchCategory: string | null
}

interface FuzzySearchOptions {
  keys: string[]
  includeScore: boolean
  threshold: number
}

export type TooltipContentProps = {
  readonly link: string
  readonly setLink: (url: string) => void
  readonly isSettingsLayer: boolean
  readonly disabled?: boolean
}

export type LinkTooltipSettingsLayerProps = {
  readonly link: string
  readonly setLink: (url: string) => void
  readonly label: string
  readonly disabled?: boolean
} & TranslateProps

interface EditorElement {
  url: string
  id: string
  title: string
  type: string
  itemType: string
  isExternal: boolean
}

export interface LinkTooltipErrorState {
  hasError: boolean
}
